import { useEffect, useRef, useState } from 'react';
import { Monitor, Moon, Palette, Settings, Sparkles, Sun, X } from 'lucide-react';
import {
  COLOR_THEMES,
  useThemeStore,
  type ColorTheme,
  type Theme,
} from '@/store/themeStore';
import { cn } from '@/lib/utils';

const MODE_OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'brand', label: 'Brand', icon: Sparkles },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * Floating, route-agnostic theme switcher.
 *
 * Mounted once at the app root so it appears on every page (including
 * onboarding, login, error states) without each layout having to render it.
 * Bottom-right by convention, collapses to a small button to keep the page
 * uncluttered.
 */
export function ThemePlugin() {
  const { theme, colorTheme, setTheme, setColorTheme } = useThemeStore();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const activeSwatch = COLOR_THEMES.find((c) => c.value === colorTheme)?.swatch;
  const activeLabel = COLOR_THEMES.find((c) => c.value === colorTheme)?.label;

  return (
    <div
      ref={popoverRef}
      // Lifted above the mobile bottom nav so the settings button does not sit
      // on top of the "More" tab; back to the corner on desktop where there is
      // no bottom bar.
      className="fixed bottom-20 right-4 md:bottom-5 md:right-5 z-[2147483000] isolate print:hidden"
      style={{
        pointerEvents: 'none',
        marginBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {open && (
        <div
          role="dialog"
          aria-label="Theme settings"
          className="pointer-events-auto mb-3 w-[280px] origin-bottom-right rounded-xl border border-border bg-card p-4 shadow-lg animate-in fade-in zoom-in-95 duration-150"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[13px] font-semibold text-foreground">Theme</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Appearance
          </p>
          <div className="mb-4 grid grid-cols-2 gap-2">
            {MODE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  aria-pressed={active}
                  className={cn(
                    'flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-[12px] font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                    active
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Accent color
            </p>
            <p className="text-[11px] text-muted-foreground">{activeLabel}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {COLOR_THEMES.map((opt) => {
              const active = colorTheme === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  title={opt.label}
                  aria-label={opt.label}
                  aria-pressed={active}
                  onClick={() => setColorTheme(opt.value as ColorTheme)}
                  className={cn(
                    'h-7 w-7 rounded-full transition-transform hover:scale-110',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                  )}
                  style={{
                    background: opt.swatch,
                    boxShadow: active
                      ? `0 0 0 2px var(--card), 0 0 0 4px ${opt.swatch}`
                      : undefined,
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open theme settings"
        aria-expanded={open}
        className={cn(
          'pointer-events-auto group relative flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card shadow-lg transition-all',
          'hover:scale-105 hover:shadow-xl',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        )}
      >
        <span
          className="absolute inset-1.5 rounded-full opacity-90 transition-opacity group-hover:opacity-100"
          style={{ background: activeSwatch }}
          aria-hidden
        />
        <Settings className="relative z-10 h-4 w-4 text-white drop-shadow" />
      </button>
    </div>
  );
}

export default ThemePlugin;
